<template>
  <div class="inquiry-detail-plan">
    <inquiry-detail-header
:createdAt="createdAt"
:asOfDate="asOfDate"
:patient="patient"
:subscriber="subscriber"
      :payer="payer"
      :payer-name-override="payerNameOverride"
      :provider="provider"
:primary-care-provider="plan?.primary_care_provider"
:reference-number="inquiryId"
/>

    <div class="container is-max-hd" v-if="remoteErrorMessages.length !== 0">
      <br />
      <div class="content">
        <b-notification type="is-warning" :closable="false" role="alert">
          Payer Error:
          <ul>
            <li v-for="message in remoteErrorMessages" :key="message">
              {{ message }}
            </li>
          </ul>
        </b-notification>
      </div>
    </div>

    <div class="container is-max-hd" v-if="alerts.length">
      <br />
      <div class="content">
        <b-notification v-for="alert in alerts" :key="alert.id" :type="notificationType(alert.color)"
          :style="notificationStyle(alert.color)" :closable="false" role="alert">
          <!-- Alert message using same font--> 
          <div class="is-size-5">
            {{ alert.message }}
          </div>

          <!-- System info (small text) -->
          <div class="is-size-7 mt-1">
            Created by {{ alert.user.first_name }} {{ alert.user.last_name }}
            ·
            Criteria:
             <span v-if="alert.alert_provider">
              Provider = {{ alert.alert_provider.name }}
            </span>
            <span v-if="alert.alert_payer">
              Payer = {{ alert.alert_payer.name }}
            </span>
            <span v-if="alert.alert_group_number">
             , Group = {{ alert.alert_group_number.group_number }}
            </span>
            <span v-if="alert.alert_group_name">
              , Group Name = {{ alert.alert_group_name.group_name }}
            </span>
            <span v-if="alert.alert_plan_name">
              , Plan Name = {{ alert.alert_plan_name.plan_name }}
            </span>
            <span v-if="alert.alert_plan_number">
              , Plan Number = {{ alert.alert_plan_number.plan_number }}
            </span>
            <span v-if="alert.alert_insurance_type">
              , Insurance = {{ alert.alert_insurance_type.name }}
            </span>
            <span v-if="alert.alert_prefix">
              , Prefix = {{ alert.alert_prefix.prefix }}
            </span>
          </div>
        </b-notification>
      </div>
    </div>

    <div class="container is-max-hd" v-if="inputDoesNotMatchMessages.length !== 0">
      <br />
      <div class="content">
        <b-notification type="is-warning" :closable="false" role="alert">
          Update your records:
          <ul>
            <li v-for="message in inputDoesNotMatchMessages" :key="message">
              {{ message }}
            </li>
          </ul>
        </b-notification>
      </div>
    </div>

    <!-- Mobile/Tablet Action Buttons (After Hero) -->
    <div class="container is-max-hd mobile-action-buttons-top">
      <div class="block action-buttons-container">
        <b-button
          tag="router-link"
          type="is-primary"
          :to="{ name: 'InquiryCopy', params: { id: inquiryId } }"
          class="action-button"
          icon-left="reload"
        >
          Re Run inquiry
        </b-button>
        <b-button
          tag="router-link"
          type="is-primary"
          :to="{ name: 'InquiryAsBlanketVOB', params: { id: inquiryId } }"
          class="action-button"
          icon-left="reload"
          v-if="canCreateBlanketVOB()"
        >
          Re Run as Blanket VOB
        </b-button>
        <b-button
          target="_blank"
          :href="pdfUrl"
          tag="a"
          type="is-primary"
          class="action-button"
          icon-left="cloud-download-outline"
        >
          Download PDF
        </b-button>
      </div>
    </div>

    <section class="section">
      <div class="container is-max-hd">
        <div class="columns">
          <div class="column is-one-quarter">
            <inquiry-detail-sidebar
              :plan="plan"
              :selected-networks="selectedNetworks"
              :selected-payments="selectedPayments"
              :selected-benefits="selectedBenefits"
              :selected-level="selectedLevel"
              :patient-history="patientHistory"
              :patient-history-loading="patientHistoryLoading"
              :selected-id="inquiryId"
              @input-network="selectedNetworks = $event"
              @input-payment="selectedPayments = $event"
              @input-benefit="selectedBenefits = $event"
              @input-level="selectedLevel = $event"
              @select-inquiry="onSelectInquiry"
              @reload-inquiries="reloadInquiries"
            />
          </div>
          <div class="column is-three-quarters">
            <div class="block action-buttons-container">
              <b-button
                tag="router-link"
                type="is-primary"
                :to="{ name: 'InquiryCopy', params: { id: inquiryId } }"
                class="action-button"
                icon-left="reload"
              >
                Re Run inquiry
              </b-button>
              <!-- check if hasBlanketVOB subscription -->
              <b-button
                tag="router-link"
                type="is-primary"
                :to="{ name: 'InquiryAsBlanketVOB', params: { id: inquiryId } }"
                class="action-button"
                icon-left="reload"
                v-if="canCreateBlanketVOB()"
              >
                Re Run as Blanket VOB
              </b-button>

              <b-button
                target="_blank"
                :href="pdfUrl"
                tag="a"
                type="is-primary"
                class="action-button"
                icon-left="cloud-download-outline"
              >
                Download PDF
              </b-button>
            </div>

            <inquiry-detail-payer
              id="inquiry-plan-information"
              :plan="plan"
              :payer="payer"
              :response-payer="responsePayer"
              :payer-name-override="payerNameOverride"
            ></inquiry-detail-payer>

            <inquiry-detail-coverage-summary
              id="inquiry-coverage-summary"
              :benefit-groups="orderBenefitGroups(plan.benefit_groups)"
              :selected-networks="selectedNetworks"
              :selected-payments="selectedPayments"
              :selected-benefits="selectedBenefits"
              :selected-level="selectedLevel"
            />

            <div
              class="block inquiry-benefit-section"
              :id="`inquiry-benefit-${i}`"
              v-for="(benefitGroup, i) in orderBenefitGroups(plan.benefit_groups)"
              :key="i"
            >
              <inquiry-detail-benefit
                :benefit-group="benefitGroup"
                :plan-notes="plan.notes"
                :selected-networks="selectedNetworks"
                :selected-payments="selectedPayments"
                :selected-benefits="selectedBenefits"
                :selected-level="selectedLevel"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import InquiryDetailBenefit from './InquiryDetailBenefit.vue';
import InquiryDetailCoverageSummary from './InquiryDetailCoverageSummary.vue';
import InquiryDetailHeader from './InquiryDetailHeader.vue';
import InquiryDetailPayer from './InquiryDetailPayer.vue';
import InquiryDetailSidebar from './InquiryDetailSidebar.vue';
import request from '../request';

export default {
  name: 'Plan',
  components: {
    InquiryDetailBenefit,
    InquiryDetailCoverageSummary,
    InquiryDetailHeader,
    InquiryDetailPayer,
    InquiryDetailSidebar,
  },
  props: {
    // Raw data passed in
    rawPlan: Object,
    createdAt: String,
    asOfDate: String,
    inquiryId: String,
    payer: Object,
    responsePayer: String,
    patient: Object,
    subscriber: Object,
    provider: String,
    payerNameOverride: String,
    patientHistory: {
      type: Array,
      default: () => [],
    },
    patientHistoryLoading: {
      type: Boolean,
      default: false,
    },
    inputDoesNotMatchMessages: {
      type: Array,
      default: () => [],
    },
    remoteErrorMessages: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters(['permReimbursements', 'user']),
    pdfUrl() {
      const baseUrl = process.env.VUE_APP_DEVELOPMENT_BACKEND_BASE_URL || '';
      let url = `${baseUrl}/api/v1/inquiries/${this.inquiryId}/pdf`;
      url += `?payments=${this.selectedPayments}`;
      url += `&networks=${this.selectedNetworks}`;
      url += `&benefits=${this.selectedBenefits}`;
      url += `&level=${this.selectedLevel}`;
      return url;
    },
    currentAlert() {
      return this.alerts[this.activeAlertIndex] || null;
    }
  },

  data() {
    return {
      selectedNetworks: ['All'],
      selectedPayments: ['All'],
      selectedBenefits: ['All'],
      selectedLevel: 'All',
      // Processed plan
      plan: this.parsePlan(),
      orderedBenefitGroups: [],
      alerts: [],
      activeAlertIndex: 0
    };
  },
  mounted() {
    this.fetchInquiryAlerts()
  },
  methods: {
    planName(plan) {
      if (!plan) return '';
      for (const bg of plan.benefit_groups || []) {
        for (const sd of bg.status_details || []) {
          if (sd.description) return sd.description;
        }
      }
      return plan.name || '';
    },
    parsePlan() {
      let copiedPlan = JSON.parse(JSON.stringify(this.rawPlan));

      // Plan has no benefit_groups so just return the raw plan with an
      // empty benefit_groups array. This is usually when there is unknown
      // coverage and the response contains contact information.
      if (!Object.hasOwn(this.rawPlan, 'benefit_groups')) {
        copiedPlan['benefit_groups'] = [];
        return copiedPlan;
      }
      return copiedPlan;
    },
    orderBenefitGroups(benefitGroups) {
      if (this.orderedBenefitGroups && this.orderedBenefitGroups.length > 0) {
        return this.orderedBenefitGroups;
      }
      if (!benefitGroups || benefitGroups.length === 0) {
        return [];
      }

      const orderMap = {
        deductibles__inNetwork__Individual: 0,
        deductibles__outOfNetwork__Individual: 1,
        deductibles__inNetwork__Family: 2,
        deductibles__outOfNetwork__Family: 3,
        outOfPocket__inNetwork__Individual: 4,
        outOfPocket__outOfNetwork__Individual: 5,
        outOfPocket__inNetwork__Family: 6,
        outOfPocket__outOfNetwork__Family: 7,
      };

      benefitGroups = benefitGroups.map((benefitGroup, i) => {
        if (benefitGroup.name?.toLowerCase() === 'health benefit plan coverage') {
          if (benefitGroup.benefits) {
            benefitGroup.benefits.map((benefit) => {
              const key = `${benefit.amount_code}__${benefit.network_code}__${benefit.level}`;
              // If the key is not in the order map, set the order to 1000
              if (orderMap[key] === undefined) {
                benefit.order = 1000;
              } else {
                benefit.order = orderMap[key];
              }
              return benefit;
            });
            // Sort benefits by order
            benefitGroup.benefits.sort((a, b) => a.order - b.order);
          } else {
            benefitGroup.benefits = [];
          }
          benefitGroup.order = 0; // Health Benefit Plan Coverage should always be first
        } else {
          benefitGroup.order = i + 1;
        }

        return {
          ...benefitGroup,
        };
      });

      this.orderedBenefitGroups = benefitGroups.sort((a, b) => a.order - b.order);
      return this.orderedBenefitGroups;
    },
    canCreateBlanketVOB() {
      if (
        this.user.has_blanket_vob &&
        (this.user.has_active_subscription || this.user.has_past_due_subscription)
      ) {
        return true;
      }

      // Check if has override access (trial/override access)
      if (this.user.has_override_blanket_vob) {
        // Check if override has expired
        if (this.user.override_blanket_vob_ends_at) {
          const expiryDate = new Date(this.user.override_blanket_vob_ends_at);
          const now = new Date();
          if (now > expiryDate) {
            return false; // Override expired
          }
        }

        // Check if usage limit has been reached
        if (this.user.override_blanket_vob_limit > 0) {
          if (this.user.override_blanket_vob_used >= this.user.override_blanket_vob_limit) {
            return false; // Limit reached
          }
        }

        return true; // Override is valid
      }

      return false;
    },
    onSelectInquiry(id) {
      this.$emit('select-inquiry', id);
    },
    reloadInquiries() {
      this.$emit('reload-inquiries');
    },
    async fetchInquiryAlerts() {
      const params = {
        check_criteria: true,
        payer_name: this.payerNameOverride || this.payer?.name || "",
        group_number: this.plan?.group_number || "",
        group_name: this.plan?.group_name || "",
        plan_number: this.plan?.number || "",
        plan_name: this.planName(this.plan),
        insurance_type: this.plan?.insurance_type || "",
        provider: this.provider || "",
        member_id: this.subscriber?.member_id || "",
      };
      try {
        const related = await request.get(`/alerts`, { params: params });
        this.alerts = related.data.alerts;
      } catch {
        this.$buefy.toast.open({
          message: 'An error occurred while fetching inquiry alerts',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingRelatedInquiries = false;
      }
    },
    notificationType(color) {
      const map = {
        red: "is-danger",
        yellow: "is-warning",
        green: "is-success",
      };

      return map[color] || "is-warning";
    },
    notificationStyle(color) {
      // If HEX or rgb → apply custom background
      if (color?.startsWith("#") || color?.startsWith("rgb")) {
        return {
          backgroundColor: color,
          color: "#fff",          // text color
          borderLeft: `4px solid ${this.darken(color, 15)}`
        }
      }

      return {}
    },

    darken(hex, percent) {
      let num = parseInt(hex.replace("#", ""), 16)
      let r = (num >> 16) - percent
      let g = ((num >> 8) & 0x00ff) - percent
      let b = (num & 0x0000ff) - percent

      r = r < 0 ? 0 : r
      g = g < 0 ? 0 : g
      b = b < 0 ? 0 : b

      return `rgb(${r}, ${g}, ${b})`
    },
  },
};
</script>

<style scoped>
.inquiry-detail-plan { background: #fafafa; }
.inquiry-benefit-section, #inquiry-plan-information, #inquiry-coverage-summary { scroll-margin-top: 5rem; }
.section { padding-top: 1.75rem; }

/* Action Buttons Container */
.action-buttons-container {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-button {
  margin: 0;
}

/* Mobile/Tablet Buttons at Top - Hidden on Desktop */
.mobile-action-buttons-top {
  display: none;
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  padding-left: 0;
  padding-right: 0;
}

.mobile-action-buttons-top .block {
  padding: 0 1rem;
  margin-bottom: 0;
}

.mobile-action-buttons-top.container.is-max-hd {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Desktop: Hide mobile buttons, show sidebar buttons */
@media screen and (min-width: 1025px) {
  .mobile-action-buttons-top {
    display: none;
  }
}

/* Mobile/Tablet View */
@media screen and (max-width: 1024px) {
  /* Show mobile buttons at top */
  .mobile-action-buttons-top {
    display: block;
  }

  /* Add horizontal padding to section and reduce top padding */
  .section {
    padding-top: 1rem;
    padding-left: 0;
    padding-right: 0;
  }

  .section .container.is-max-hd {
    padding-left: 0;
    padding-right: 0;
  }

  /* Stack columns vertically - Sidebar above content */
  .columns {
    display: flex;
    flex-direction: column;
    margin-left: 0;
    margin-right: 0;
  }

  .column.is-one-quarter,
  .column.is-three-quarters {
    width: 100%;
    max-width: 100%;
    padding-left: 0;
    padding-right: 0;
  }

  /* Add consistent margins to content boxes to match buttons */
  .column.is-three-quarters .box {
    margin-left: 1rem;
    margin-right: 1rem;
    width: calc(100% - 2rem);
  }

  /* Sidebar appears first */
  .column.is-one-quarter {
    order: 1;
  }

  /* Content appears second */
  .column.is-three-quarters {
    order: 2;
  }

  /* Hide desktop buttons in sidebar */
  .column.is-three-quarters > .block.action-buttons-container {
    display: none;
  }

  /* Full width buttons */
  .action-buttons-container {
    justify-content: stretch;
    flex-direction: column;
  }

  .action-button {
    width: 100%;
  }
}
</style>
